import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Multiline fenced-code-block renderer for channel messages (KTK-272).
//
// Why this exists, even though MarkdownContent already had a `code` override:
// react-markdown v9 stopped reliably propagating an `inline` flag to the
// `code` component, so the previous code path used "language tag = block,
// no language = inline" as a heuristic. That mis-classified bare ``` ... ```
// fences (no language suffix) as inline code, producing per-line
// inline-code-styled spans inside a `<pre>` instead of one block.
//
// The fix is to override `pre` directly: react-markdown only emits `<pre>`
// for fenced blocks, so its presence is a definitive signal. We pull the
// language and raw text out of the hast node attached to the `pre`, and
// render this component. The `code` override becomes inline-only.
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useState } from "react";
import { useThemeMode } from "./useThemeMode.js";
// Hover visibility lives in CSS rather than React state. JS-state hover
// proved flaky here: hovering the absolutely-positioned button *briefly*
// also exits the natural document flow region of the outer div in some
// scroll positions, and we saw the button fade even though the cursor
// hadn't actually left the block. The native :hover selector is the
// single source of truth instead. The `--copied` modifier pins the
// button visible through the feedback window so the cursor wandering
// off mid-animation doesn't strand a half-faded button.
const STYLE_ID = "kritaka-message-code-block";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = `
    .kritaka-mcb__btn {
      opacity: 0;
      transition: opacity 120ms ease-out, background 80ms ease-out;
    }
    .kritaka-mcb:hover .kritaka-mcb__btn,
    .kritaka-mcb__btn.is-copied {
      opacity: 1;
    }
  `;
    document.head.appendChild(el);
}
export function MessageCodeBlock({ lang, children }) {
    const [copied, setCopied] = useState(false);
    // KTK-384 — the highlighter's palette is a JS object, not CSS, so it
    // can't follow the theme tokens on its own. Both branches must flip
    // together: surface via --bg-inset, syntax colors via the style pick,
    // or one theme gets unreadable text (dark-on-dark for bare fences in
    // light, light-on-light for tagged fences on a light surface).
    const theme = useThemeMode();
    const onCopy = () => {
        navigator.clipboard?.writeText(children).catch(() => undefined);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    };
    const useHighlighter = Boolean(lang) && lang !== "text";
    return (_jsxs("div", { className: "kritaka-mcb", "data-language": lang ?? "text", style: {
            position: "relative",
            margin: "8px 0",
            borderRadius: 6,
            border: "1px solid var(--border)",
            background: "var(--bg-inset, #0d0e11)",
            overflow: "hidden",
        }, children: [_jsx("div", { style: {
                    position: "sticky",
                    top: 12,
                    height: 0,
                    zIndex: 1,
                    pointerEvents: "none",
                }, children: _jsx("button", { type: "button", onClick: onCopy, "aria-label": copied ? "Copied" : "Copy code", className: `kritaka-mcb__btn${copied ? " is-copied" : ""}`, style: {
                        position: "absolute",
                        top: 0,
                        right: 12,
                        width: 26,
                        height: 26,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        color: copied ? "#1a1100" : "var(--text-secondary)",
                        background: copied
                            ? "#f59f00"
                            : "color-mix(in oklab, var(--bg-inset, #0d0e11) 85%, transparent)",
                        border: "1px solid var(--border)",
                        borderRadius: 4,
                        cursor: "pointer",
                        pointerEvents: "auto",
                        backdropFilter: "blur(4px)",
                    }, children: copied ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {}) }) }), useHighlighter ? (_jsx(SyntaxHighlighter, { style: (theme === "light" ? oneLight : vscDarkPlus), language: normalizeLang(lang), PreTag: "div", customStyle: {
                    margin: 0,
                    padding: "12px 16px",
                    fontSize: 12,
                    background: "transparent",
                }, codeTagProps: {
                    style: {
                        fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                        // oneLight paints its own near-white background on the code
                        // tag (vscDarkPlus doesn't) — flatten both onto --bg-inset.
                        background: "transparent",
                    },
                }, children: children })) : (_jsx("pre", { style: {
                    margin: 0,
                    padding: "12px 16px",
                    fontSize: 12,
                    color: "var(--text-primary)",
                    fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
                    whiteSpace: "pre",
                    overflowX: "auto",
                }, children: _jsx("code", { children: children }) }))] }));
}
function CopyIcon() {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", stroke: "currentColor", strokeWidth: "1.3", children: [_jsx("rect", { x: "4", y: "4", width: "7", height: "7", rx: "1" }), _jsx("path", { d: "M2.5 8.5V3.5a1 1 0 0 1 1-1H8" })] }));
}
function CheckIcon() {
    return (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M3 7.5L6 10.5L11 4.5" }) }));
}
// Prism's Prism build wants long names ("javascript", "typescript", etc).
// Markdown messages frequently use the short variants ("js"/"ts"/"py"/"sh")
// so map those. Anything else passes through; Prism falls back to plain
// rendering when it doesn't know the language.
function normalizeLang(lang) {
    switch (lang) {
        case "js":
            return "javascript";
        case "ts":
            return "typescript";
        case "py":
            return "python";
        case "sh":
            return "bash";
        default:
            return lang;
    }
}
//# sourceMappingURL=MessageCodeBlock.js.map